/**
 * The lesson-package catalogue — owns its data and every state, no page
 * header, so a tabbed Lessons page can mount it as a panel. Cards render in
 * server order (price ASC) — no client sort.
 */

import { EmptyState } from '@butter/ui/components/feedback';
import { MembershipCard } from '@butter/ui/domains/subscriptions';
import { ArrowIcon, GraduationIcon } from '@butter/ui/icons';
import { useTenantNavigate } from '~/lib/navigation';
import { useLessons, formatLessonHours } from '~/services/internalApi/lessons';
import type { Lesson } from '~/services/internalApi/lessons';
import { useClubAccess, CLUB_NO_ACCESS_LABEL, CLUB_NO_ACCESS_MESSAGE } from '~/services/internalApi/club';
import { QueryParams } from '~/lib/queryParams';

const lessonsIcon = <GraduationIcon aria-hidden className="size-8 text-butter-gray-700" />;

export function LessonsCatalogue() {
  const navigate = useTenantNavigate();
  const { status: clubAccessStatus } = useClubAccess();
  const { lessons, hasAnswer, failure, isError, refetch } = useLessons();

  const handleSelect = (lesson: Lesson) => {
    // tenant-handle + club-id are preserved automatically; `?lesson=` is the
    // durable payload for the guest sign-in round trip, set explicitly.
    navigate(`/lessons/purchase?${QueryParams.LESSON}=${lesson.id}`, { state: { lesson } });
  };

  // Not enrolled here → no purchase. Mirror the subscription flow's gate.
  if (clubAccessStatus === 'no-access') {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center" data-testid="lessons-no-access">
        <p className="text-base font-medium text-fg-primary">{CLUB_NO_ACCESS_LABEL}</p>
        <p className="mt-2 text-sm text-fg-quaternary">{CLUB_NO_ACCESS_MESSAGE}</p>
      </div>
    );
  }

  const loadFailed = !!failure || isError;

  return (
    <>
      <h2 className="mb-6 text-2xl font-semibold text-butter-gray-800" data-testid="lessons-available-header">
        Available lesson packages
      </h2>

      {!hasAnswer && !isError && (
        <div role="status" aria-live="polite">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-60 animate-pulse rounded-3xl bg-butter-gray-100"
                aria-hidden="true"
                data-testid={`lessons-loading-card-${i}`}
              />
            ))}
          </div>
          <span className="sr-only">Loading lesson packages…</span>
        </div>
      )}

      {loadFailed && (
        <div role="alert">
          <EmptyState
            variant="muted"
            icon={lessonsIcon}
            title="Couldn't load lesson packages"
            description={failure?.message ?? 'Please check your connection and try again.'}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                data-testid="lessons-retry-btn"
                className="btn btn-primary btn-xl"
              >
                Try again
              </button>
            }
            data-testid="lessons-error-message"
          />
        </div>
      )}

      {hasAnswer && !loadFailed && lessons.length === 0 && (
        <EmptyState
          variant="muted"
          icon={lessonsIcon}
          title="No lesson packages yet"
          description="This club isn't selling lesson packages right now. Check back soon, or ask the front desk about lessons."
          data-testid="lessons-empty"
        />
      )}

      {hasAnswer && !loadFailed && lessons.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {lessons.map((lesson) => (
            <MembershipCard
              key={lesson.id}
              membership={{
                id: lesson.id,
                name: lesson.name,
                description: lesson.description,
                color: lesson.color,
                price: lesson.price,
                billingFrequency: 'one-time',
              }}
              size="lg"
              headerLabel={formatLessonHours(lesson.maxTotalMinutes)}
              onClick={() => handleSelect(lesson)}
              overlayLabel={`Select ${lesson.name}`}
              opensDialog={false}
              action={
                <button
                  type="button"
                  onClick={() => handleSelect(lesson)}
                  aria-label={`Select ${lesson.name}`}
                  data-testid={`lesson-card-select-${lesson.id}`}
                  className="flex size-9 items-center justify-center rounded-full bg-theme-600 text-white transition-colors text-interactive:bg-theme-700"
                >
                  <ArrowIcon aria-hidden className="size-6 -rotate-45" />
                </button>
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
